import React, { memo, useRef } from 'react';
import cn from 'classnames';
import { ReactComponent as ProfileIcon } from '@assets/images/profile_icon.svg';
import { ReactComponent as Cross } from '@assets/images/cross.svg';
import { ReactComponent as Clock } from '@assets/images/Clock.svg';
import PhoneNumberComponent from '@components/common/PhoneNumberComponent/indexRevamp';
import SimpleReactSelect from '@formElements/SimpleReactSelect/indexRevamp';
import CustomKeyboard from '@components/common/CustomKeyboardCopy/indexRevamp';
import { TouchableButton } from '@components/common/Touchable/index';
import Styles from '@containers/StoreChecks/OthersPopUpScreen/togo.module.scss';
import { LABEL } from '@veriables';

// ─── pure helpers ──────────────────────────────────────────────────────────────

export function prependZero(n) {
  return n < 10 ? `0${n}` : n;
}

export function formatTimeLabel(raw) {
  const hour = Number(raw.split(':')[0]);
  const min = raw.split(':')[1];
  if (hour === 0) return `12:${min} AM`;
  if (hour === 12) return `12:${min} PM`;
  if (hour > 12) return `${prependZero(hour - 12)}:${min} PM`;
  return `${prependZero(hour)}:${min} AM`;
}

export function formatPickupTime(time) {
  if (!time) return '';
  const [clockPart, period] = time.split(' ');
  const hourStr = clockPart.substring(0, 2);
  const rest = clockPart.substring(2);
  if (period === 'PM') {
    return hourStr !== '12' ? String(Number(hourStr) + 12) + rest : clockPart;
  }
  if (hourStr === '12') return Number(hourStr) - 12 + '0' + rest;
  return clockPart;
}

export function itemsFilter(items = []) {
  return items
    .filter(e => e.pending)
    .map(e => ({ ...e, ...JSON.parse(e.orderJson) }));
}

const toSelectOpts = items => items.map(item => ({ id: item, name: item }));

// ─── field components ──────────────────────────────────────────────────────────

export const NameField = memo(
  ({ value, onChange, onClear, onFocus, theme, isPortrait }) => (
    <div
      className={cn(isPortrait ? 'col-12 mt-4' : 'mt-4 row')}
      style={{ display: 'flex', justifyContent: 'center' }}
    >
      <div
        style={{ position: 'relative' }}
        className={cn(isPortrait ? 'w-100' : '')}
      >
        <input
          id="checkName"
          name="checkName"
          type="text"
          value={value}
          placeholder="Enter Guest Name"
          className={cn(
            'h-200',
            isPortrait ? 'w-100' : '',
            isPortrait ? Styles.inputFieldPortrait : Styles.inputField
          )}
          style={{
            backgroundColor: theme.input_box_background,
            borderBottom: theme?.inputBorder_Bottom,
            color: theme.primary_text,
            '--placeholder-color': theme?.input_placeholder,
          }}
          onChange={onChange}
          onFocus={onFocus}
        />
        <div
          style={{
            position: 'absolute',
            top: isPortrait ? '16px' : '8px',
            left: '12px',
          }}
        >
          <ProfileIcon height="1.6rem" width="1.6rem" fill={theme.iconcolor} />
        </div>
        <div
          style={{
            position: 'absolute',
            right: '6px',
            top: isPortrait ? '15px' : '6px',
          }}
          tabIndex="0"
          role="button"
          onClick={onClear}
        >
          <Cross fill={theme.iconcolor} />
        </div>
      </div>
    </div>
  )
);

export const PhoneField = memo(
  ({
    value,
    countryCode,
    onChange,
    onClear,
    onFocus,
    onCountryCodeChange,
    theme,
    isPortrait,
    isLandScape,
    isTogo,
    isBrowser,
  }) => (
    <div
      className={cn('text-center mt-3', isPortrait ? 'col-12' : 'row')}
      style={{ display: 'flex', justifyContent: 'center', position: 'relative' }}
    >
      <PhoneNumberComponent
        countryCode={countryCode}
        handleCountryCodeChange={e => onCountryCodeChange(e.id)}
        styles={{
          width: '19%',
          left: !isPortrait ? '87px' : '30px',
          height: '44px',
          padding: 'p-2',
          zIndex: 9999,
          lineHeight: 'normal',
          top: isPortrait ? '10px' : '0',
        }}
        GuestInformationPhone={true}
        serverAppTheme={theme}
        isLandScape={isLandScape}
        screenType="callCenterLogin"
        isTogo={isTogo}
        isOrderingPortrait={isPortrait}
      />
      <input
        id="phoneNumber"
        name="phoneNumber"
        value={value}
        placeholder="Enter Phone Number"
        maxLength={countryCode === '1' ? (isBrowser ? '12' : '11') : '15'}
        className={cn(
          'h-200',
          isPortrait ? 'w-100' : '',
          isPortrait ? Styles.inputFieldPortrait : Styles.inputField
        )}
        style={{
          backgroundColor: theme.input_box_background,
          borderBottom: theme?.inputBorder_Bottom,
          color: theme.primary_text,
          '--placeholder-color': theme?.input_placeholder,
        }}
        onChange={onChange}
        onFocus={onFocus}
        autoComplete="off"
      />
      <div
        style={{
          position: 'absolute',
          right: isPortrait ? '20px' : '107px',
          top: isPortrait ? '15px' : '6px',
        }}
        tabIndex="0"
        role="button"
        onClick={onClear}
      >
        <Cross fill={theme.iconcolor} />
      </div>
    </div>
  )
);

export const PickupTimeField = memo(
  ({ value, options, onChange, theme, isPortrait }) => {
    const dropDownRef = useRef(null);
    const isSingle = options.length === 1;
    const fieldStyle = {
      backgroundColor: theme.input_box_background,
      borderBottom: theme?.inputBorder_Bottom,
      color: theme.primary_text,
    };
    return (
      <div
        className={cn(
          'justify-content-center text-center mt-3',
          isPortrait ? 'col-12' : 'row'
        )}
      >
        {!isSingle ? (
          <div
            className={cn(
              'position-relative',
              isPortrait ? Styles.inputFieldPortrait : Styles.inputField,
              Styles.textIndentNone,
              isPortrait ? 'w-100' : ''
            )}
            style={fieldStyle}
          >
            <SimpleReactSelect
              className={cn(
                'fs-12 text-start px-2',
                isPortrait
                  ? Styles.SimpleReactPortrait
                  : Styles.SimpleReactSelectHeight
              )}
              style={{
                position: 'relative',
                fontSize: '3.5vh',
                height: '10vm',
                lineHeight: '5vm',
                left: '150px',
                marginLeft: '-240px',
              }}
              ref={dropDownRef}
              maxMenuHeight={140}
              onChange={data => onChange(data ? data.id : '')}
              options={toSelectOpts(options)}
              name="pickupTime"
              defaultValue={value}
              placeholder="Choose Pickup Time"
              serverAppTheme={theme}
              screenType="callCenter"
              GuestInformation={true}
            />
            <div
              style={{
                position: 'absolute',
                top: isPortrait ? '15px' : '5px',
                left: '10px',
              }}
            >
              <Clock height="1.9rem" width="1.9rem" fill={theme.iconcolor} />
            </div>
          </div>
        ) : (
          <div className="text-center row position-relative">
            <input
              value={options[0]}
              type="text"
              readOnly
              className={cn(
                'h-200 opacity-30',
                isPortrait ? Styles.inputFieldPortrait : Styles.inputField
              )}
              style={{
                borderBottom: theme?.inputBorder_Bottom,
                backgroundColor: theme.input_box_background,
                textIndent: '153px',
                color: theme.primary_text,
              }}
            />
            <div className={cn('position-absolute', Styles.clockIcon)}>
              <Clock height="1.9rem" width="1.9rem" fill={theme.iconcolor} />
            </div>
          </div>
        )}
      </div>
    );
  }
);

export const KeyboardSection = memo(
  ({
    keyboardRef,
    inputName,
    typed,
    onKeyboardInput,
    onClose,
    isPortrait,
    isLandScape,
    maxLength,
  }) => (
    <div
      ref={keyboardRef}
      className={cn(
        'p-0 m-0',
        Styles.keyboard1,
        isPortrait ? Styles.keyBoardPortrait : Styles.keyboardLandScape
      )}
    >
      <div
        className={cn('col-12 p-0 text-right position-absolute', Styles.arrow)}
      />
      <CustomKeyboard
        type={inputName === 'phoneNumber' ? 'numericAlpha' : 'textDefaultCaps'}
        onClickBtn={key => onKeyboardInput(key, inputName)}
        typed={typed}
        closeKeyboard={onClose}
        btnSize="fs-18"
        customClass="h-auto mt-auto p-2"
        spacebtn={!isLandScape && Styles.spacebtn}
        screenType="checkCreate"
        isAlphaNumericFlag={true}
        tableKeyHeight={true}
        callApi={true}
        maxLength={inputName === 'phoneNumber' ? maxLength : undefined}
      />
    </div>
  )
);

export const ActionButtons = memo(
  ({ onCloseModal, onContinue, theme, isPortrait }) => (
    <div
      className={cn(
        'col-12 d-flex align-items-end px-0 justify-content-center position-fixed bottom-0 start-0 mt-4',
        Styles.btnPosition2
      )}
    >
      <div className={cn('pr-1', isPortrait ? 'col-6' : 'col-5')}>
        <TouchableButton
          type="button"
          className={cn(
            'px-0 mb-3 mt-2 rounded btn-ripple fs_24 fw-600 w-100',
            isPortrait ? 'btn68' : 'btn44'
          )}
          style={{
            backgroundColor: theme?.OFF_Premise_Btn,
            border: theme?.cancleBtn_Border,
            color: theme?.cancleBtn_font,
          }}
          onClick={onCloseModal}
          rippleEffect={true}
        >
          {LABEL().CANCEL}
        </TouchableButton>
      </div>
      <div className={cn('pl-1', isPortrait ? 'col-6' : 'col-5')}>
        <TouchableButton
          type="submit"
          className={cn(
            'btn-ripple fs_24 fw-600 w-100 d-flex align-items-center justify-content-center mb-3 mt-2',
            isPortrait ? 'btn68' : 'btn44'
          )}
          style={{
            backgroundColor: theme?.secondaryBtn,
            color: theme?.btn_font_color,
          }}
          onClick={onContinue}
          rippleEffect={true}
        >
          {LABEL().CONTINUE}
        </TouchableButton>
      </div>
    </div>
  )
);
